import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 180000;

const getTokenDiagnostics = () => {
  const token = localStorage.getItem('prainte_token');
  if (!token) return { token_state: 'absent' };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const nowSec = Date.now() / 1000;
    return {
      token_state: payload.exp < nowSec ? 'expired' : 'valid',
      expires_in_sec: Math.round(payload.exp - nowSec),
      user_id: payload.sub ?? payload.user_id,
    };
  } catch {
    return { token_state: 'malformed' };
  }
};

let redirectingToLogin = false;

// Cliente Axios configurado
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de Request - Adiciona token de autenticação
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('prainte_token');

    if (token && config.headers) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;

        if (payload.exp < currentTime) {
          localStorage.removeItem('prainte_token');
          localStorage.removeItem('prainte_user');
          localStorage.removeItem('prainte_theme');
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('prainte_') || key.startsWith('auth')) {
              localStorage.removeItem(key);
            }
          });
          if (!redirectingToLogin) {
            redirectingToLogin = true;
            toast.error('Sessão expirada. Redirecionando para o login...');
            setTimeout(() => { window.location.href = '/login'; }, 1200);
          }
          return Promise.reject(new Error('Token expirado - bloqueando requisição'));
        }
        
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        localStorage.removeItem('prainte_token');
        localStorage.removeItem('prainte_user');
        localStorage.removeItem('prainte_theme');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('prainte_') || key.startsWith('auth')) {
            localStorage.removeItem(key);
          }
        });
        if (!redirectingToLogin) {
          redirectingToLogin = true;
          toast.error('Sessão inválida. Redirecionando para o login...');
          setTimeout(() => { window.location.href = '/login'; }, 1200);
        }
        return Promise.reject(new Error('Token inválido - bloqueando requisição'));
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de Response - Tratamento global de erros
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {

    // NÃO redirecionar se for erro 401 do endpoint /token (tentativa de login falha)
    const isTokenEndpoint = error.config?.url === '/token' || error.config?.url?.endsWith('/token');

    if (error.response?.status === 401 && !isTokenEndpoint) {
      localStorage.removeItem('prainte_token');
      localStorage.removeItem('prainte_user');
      localStorage.removeItem('prainte_theme');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('prainte_') || key.startsWith('auth')) {
          localStorage.removeItem(key);
        }
      });
      setTimeout(() => { window.location.href = '/login'; }, 100);
      return Promise.reject(new Error('Sessão expirada - redirecionando para login'));
    }

    const skipErrorToast = (error.config?.headers as Record<string, string> | undefined)?.[
      'X-Skip-Error-Toast'
    ] === 'true';

    if (error.response?.status === 403 && !skipErrorToast) {
      const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
      let message = 'Você não tem permissão para realizar esta ação.';
      if (typeof detail === 'string') {
        if (detail.includes('Sua assinatura expirou')) {
          message = 'Sua assinatura expirou. Recursos de IA não estão disponíveis. Por favor, renove sua assinatura para continuar usando IA.';
        } else if (detail.includes('Você não pode convidar usuários com permissão maior que a sua')) {
          message = detail;
        } else if (detail.trim()) {
          message = detail;
        }
      }
      toast.error(message);
    }

    if (error.response?.status === 429 && !skipErrorToast) {
      toast.error('Muitas solicitações à IA. Por favor, aguarde alguns minutos e tente novamente.');
    }

    if (error.response?.status && error.response.status >= 500 && !skipErrorToast) {
      // 502 = falha tratada da IA (ex: não conseguiu gerar achados): o backend já manda
      // uma mensagem estável e amigável em `detail` — mostrar essa, não a genérica.
      const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
      const friendly502 = error.response.status === 502 && typeof detail === 'string' && detail.trim() && detail.length < 200;
      toast.error(friendly502
        ? (detail as string)
        : 'Erro no servidor. Tente novamente — se persistir, recarregue a página ou faça login novamente.');
    }

    if (error.code === 'ECONNABORTED' && !skipErrorToast) {
      const isAiEndpoint = error.config?.url && /\/(generate|elaborate|improve|evaluate|checklist|concept-map|roadmap.*ai|chat)/i.test(error.config.url);
      toast.error(isAiEndpoint
        ? 'A geração demorou mais que o esperado. Tente novamente — geração de textos longos pode levar até 3 minutos.'
        : 'Tempo de requisição excedido. Tente novamente.'
      );
    }

    if (error.message === 'Network Error' && !skipErrorToast) {
      const isAiEndpoint = error.config?.url && /\/(generate|elaborate|improve|evaluate|checklist|concept-map|roadmap.*ai|chat)/i.test(error.config.url);
      toast.error(isAiEndpoint
        ? 'A conexão foi interrompida durante a geração. Tente novamente em alguns instantes.'
        : 'Erro de conexão. Verifique sua internet e tente novamente.'
      );
    }

    const shouldCaptureInSentry =
      (!isTokenEndpoint &&
        error.response?.status !== 401 &&
        error.response?.status !== 403 &&
        error.response?.status != null &&
        error.response.status >= 500) ||
      error.code === 'ECONNABORTED' ||
      error.message === 'Network Error';

    if (shouldCaptureInSentry) {
      Sentry.captureException(error, {
        tags: {
          api_endpoint: error.config?.url,
          http_method: error.config?.method,
          http_status: error.response?.status,
        },
        extra: {
          response_data: error.response?.data,
          error_code: error.code,
          page_url: window.location.pathname,
          time_since_load_ms: Math.round(Date.now() - performance.timeOrigin),
          ...getTokenDiagnostics(),
        },
      });
    }

    return Promise.reject(error);
  }
);

// Helper para capturar erro no Sentry e extrair mensagem
export const handleServiceError = (error: any, context?: { feature?: string; action?: string; [key: string]: any }): never => {
  console.error('[Service Error]', error);

  // Capturar no Sentry (exceto erros esperados)
  const isExpectedError = 
    error.response?.status === 401 || // Login inválido
    error.response?.status === 403 || // Sem permissão
    error.response?.status === 404;   // Não encontrado (pode ser esperado)

  if (!isExpectedError) {
    Sentry.captureException(error, {
      tags: {
        service_error: true,
        feature: context?.feature || 'unknown',
        action: context?.action || 'unknown',
        http_status: error.response?.status,
      },
      extra: {
        error_context: context,
        response_data: error.response?.data,
        error_code: error.code,
      },
      level: error.response?.status >= 500 ? 'error' : 'warning',
    });
  }

  throw new Error(getErrorMessage(error));
};

// Helper para extrair mensagem de erro do backend
export const getErrorMessage = (error: any): string => {
  // Erro com detail string
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') {
      const detail = error.response.data.detail;
      
      // Tratamento específico para mensagens conhecidas do backend
      if (detail.includes('Usuário desativado')) {
        return 'Usuário desativado. Entre em contato com o administrador.';
      }
      
      if (detail.includes('Sua assinatura expirou')) {
        return 'Sua assinatura expirou. Recursos de IA não estão disponíveis. Por favor, renove sua assinatura para continuar usando IA. Você continua com acesso apenas leitura às suas propostas.';
      }
      
      if (detail.includes('Você só pode ver usuários da sua organização')) {
        return 'Você só pode ver usuários da sua organização.';
      }
      
      if (detail.includes('Você não pode convidar usuários com permissão maior que a sua')) {
        return detail;
      }
      
      return detail;
    }

    // Erro de validação Pydantic (array)
    if (Array.isArray(error.response.data.detail)) {
      const firstError = error.response.data.detail[0];
      return firstError?.msg || 'Erro de validação';
    }
  }

  // Erro 401 Unauthorized
  if (error.response?.status === 401) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.includes('Usuário desativado')) {
      return 'Usuário desativado. Entre em contato com o administrador.';
    }
    return 'Email ou senha incorretos.';
  }

  // Erro genérico
  if (error.message) {
    return error.message;
  }

  return 'Erro desconhecido';
};

// Helper para upload de arquivos
export const uploadFile = async (
  endpoint: string,
  file: File,
  additionalData?: Record<string, any>
) => {
  const formData = new FormData();
  formData.append('file', file);

  if (additionalData) {
    Object.keys(additionalData).forEach((key) => {
      // Se o valor for objeto, converte para JSON string
      const value = additionalData[key];
      if (typeof value === 'object' && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });
  }

  return api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Helper para upload com progresso
export const uploadFileWithProgress = async (
  endpoint: string,
  file: File,
  onProgress: (progress: number) => void,
  additionalData?: Record<string, any>
) => {
  const formData = new FormData();
  formData.append('file', file);

  if (additionalData) {
    Object.keys(additionalData).forEach((key) => {
      const value = additionalData[key];
      if (typeof value === 'object' && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });
  }

  return api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};

// Helper para download de arquivos (PDF, DOCX, etc)
export const downloadFile = async (
  endpoint: string,
  filename: string
): Promise<void> => {
  try {
    const response = await api.get(endpoint, {
      responseType: 'blob',
    });

    // Criar URL temporária para o blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Limpar
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error('Erro ao baixar arquivo');
    throw error;
  }
};

export default api;
